import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";

// Images
import heroImage from "@/assets/paver-patio-installation-livingston-essex-county-nj.webp";
import poolImage from "@/assets/paver-patio-by-pool-kinnelon-nj.webp";
import springFallImage from "@/assets/paver-patio-installation-north-haledon-nj.webp";
import northHaledonPatioImage from "@/assets/mp-patio-paver-installation-north-haledon-passaic-county-nj-04052026.webp";
import veronaImage from "@/assets/patio-paver-installation-verona-nj.webp";
import pequannockImage from "@/assets/paper-patio-installation-pequannock-nj-2.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

const faqs = [
  {
    question: "How long does paver patio installation take in North Jersey?",
    answer: "Most residential patios take 2 to 5 days to complete. The timeline depends on the size of the patio and any custom features like seating walls or fire pits. Weather can also play a role, especially if we're working around rain in Bergen or Essex counties.",
  },
  {
    question: "Do I need a permit for a paver patio in North Jersey?",
    answer: "Most municipalities in North Jersey require a land use application for patio projects. Setback requirements vary by town, so it's worth checking with your local building department before the work begins. We can help point you in the right direction.",
  },
  {
    question: "What is the best paver material for North Jersey weather?",
    answer: "Concrete pavers rated for 50 or more freeze-thaw cycles perform best in our climate. They hold up through harsh winters and won't crack the way poured concrete can. Homeowners in Morris and Passaic counties have seen great results with these materials.",
  },
  {
    question: "Can you install a paver patio over my existing concrete slab?",
    answer: "Yes, if the slab is level and in stable condition. Pavers add a few inches of height to the surface, so we check door clearances and transitions before moving forward. It can be a cost-effective option for the right situation.",
  },
  {
    question: "How do I maintain my paver patio after installation?",
    answer: "Periodic cleaning, topping off the joint sand as needed, and resealing every 7 to 15 years will extend the life of your patio. We recommend a pressure wash each spring to remove dirt and debris that builds up over the winter months.",
  },
  {
    question: "Should I hire an ICPI-certified contractor for my North Jersey patio?",
    answer: "ICPI certification confirms that a contractor has been trained on proper base preparation, drainage, and installation techniques. It's one way to know the crew working on your property understands how to build for our local conditions.",
  },
];

const citations = [
  {
    num: 1,
    text: "Interlocking Concrete Pavement Institute (ICPI), Tech Spec 2: Construction of Interlocking Concrete Pavement – Base preparation standards for patios.",
    url: "https://www.icpi.org",
  },
  {
    num: 2,
    text: "ASTM International, ASTM C936: Standard Specification for Solid Concrete Interlocking Paving Units – Paver compressive strength requirements (8,000 PSI minimum).",
    url: "https://www.astm.org",
  },
  {
    num: 3,
    text: "New Jersey Uniform Construction Code, Residential Code Table R301.2 – Frost line depth for Northern New Jersey.",
    url: "https://www.nj.gov/dca/divisions/codes",
  },
  {
    num: 4,
    text: "National Association of Realtors, 2023 Remodeling Impact Report – 9.7/10 joy score for patios, approximately 60% cost recovery at resale.",
    url: "https://www.nar.realtor",
  },
  {
    num: 5,
    text: "ASCE/ICPI, Standard 58-16: Structural Design of Interlocking Concrete Pavement – Structural design standards for interlocking concrete pavement systems.",
    url: "https://www.icpi.org",
  },
];

const youtubeShorts = [
  {
    url: "https://www.youtube.com/embed/Po36ZvNh7ro",
    title: "Paver Patio Installation Video 1",
  },
  {
    url: "https://www.youtube.com/embed/KFgLmBVqRZQ",
    title: "Paver Patio Installation Video 2",
  },
  {
    url: "https://www.youtube.com/embed/_8_1uw8ZHAk",
    title: "Paver Patio Installation Video 3",
  },
];

export default function PaverPatios() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Patio Installation Totowa, NJ | Masonry Pro Construction</title>
        <meta property="og:description" content="Want a paver patio that stays level through NJ winters? We handle base work, drainage, and layout. Serving Passaic, Bergen, Essex, and Morris Counties." />
        <meta 
          name="description" 
          content="Want a paver patio that stays level through NJ winters? We handle base work, drainage, and layout. Serving Passaic, Bergen, Essex, and Morris Counties." 
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Paver Patio Installation",
            description: "Professional paver patio installation in North Jersey with proper drainage and ICPI-standard base work.",
            url: "/paving-services/paver-patios",
            image: "https://masonry-pro.com/og/paver-patios.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/paver-patios.jpg" />
        <meta property="og:title" content="Paver Patio Installation Totowa, NJ | Masonry Pro Construction" />
        <meta name="twitter:title" content="Paver Patio Installation in Totowa, NJ | Masonry Pro" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/paver-patios.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section - Centered with lighter overlay */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Paver patio installation in Livingston, Essex County, NJ" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Local Paver Patio Installation in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 1.5rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Creating beautiful, long-lasting outdoor living spaces built for North Jersey weather
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="outdoor-living" packageName="Outdoor Living Package" blurb={"Full backyard living area: paver patio, low seating wall, and built-in fire pit, engineered together for North Jersey freeze-thaw."} />

          {/* Intro Section with Citation */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  In North Jersey, a paver patio extends your living space into the backyard. We design and install custom patios for homes across <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. Most projects finish in just a few days. Our insured masonry team follows <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink> on every job. The result is a patio built to last for decades. Patios are one of the most requested projects for our <Link to="/" className="text-secondary hover:underline">masonry contractor team in North Jersey</Link>.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  Backyards around Totowa are rarely flat, and that matters more than most homeowners expect, because a patio has to sit level even when the ground under it does not. We see this constantly on the older lots in Totowa, Little Falls, and Woodland Park, where the grade can drop a couple of feet across the span of a patio. When the slope gets steep enough, we will usually add a low wall or a single step so the space stays usable, and that does add to the cost. It is worth doing, though, since building a patio on fill that was never properly compacted is what causes the settling you see a few years later. You can watch two of our recent patios, a <Link to="/videos/pool-paver-patio-outdoor-kitchen-wayne-nj" className="text-secondary hover:underline">pool paver patio in Wayne</Link> and an <Link to="/videos/paver-patio-livingston-nj" className="text-secondary hover:underline">oversized paver patio in Livingston</Link>, from start to finish.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  Before requesting your free estimate, it helps to understand <Link to="/pro-tips/paver-patio-cost-north-jersey" className="text-secondary hover:underline">what really drives paver patio cost in North Jersey</Link>, from material choices to base prep.
                </p>
              </div>
            </div>
          </section>

          {/* Solid Gravel Base - Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    A Solid Gravel Base <span className="text-gradient-gold">Prevents Shifting and Settling</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Your patio needs a strong base. We use crushed gravel on every job. When packed down, the gravel locks together. This stops pavers from sinking or moving over time.
                    </p>
                    <p>
                      North Jersey soil <CitationLink num={3} url="https://www.nj.gov/dca/divisions/codes">freezes deep in winter</CitationLink>. We dig down to account for that. Clay soil is common in <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link> and <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link> counties. We lay fabric under the gravel to keep clay out. ICPI sets standards for base depth based on soil conditions and project type.<CitationLink num={1} url="https://www.icpi.org"></CitationLink> We follow those guidelines on every install.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={pequannockImage} 
                    alt="New paver patio installation with a gazebo and outdoor furniture in Pequannock Township, Morris County, NJ" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Ground Preparation - Image LEFT, Content RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={springFallImage} 
                    alt="Paver patio installation in North Haledon, NJ showing proper base preparation" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Proper Ground Preparation <span className="text-gradient-gold">Determines Patio Longevity</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We start most paver patio projects by excavating below the finished grade. The exact depth depends on soil conditions, drainage needs, and whether we're building new or doing an overlay. That gives room for gravel, sand, and the pavers on top. The ground is sloped away from your home, so water drains away from the property.
                    </p>
                    <p>
                      We then pack the base in layers, tamping each layer down before the next one goes in. Homeowners in Morristown and Millburn ask us about this all the time. Takes longer, but your patio won't sink.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Year-Round Installation Section - Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    We Install Paver Patios <span className="text-gradient-gold">Year-Round</span> in North Jersey
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We install patios throughout the year here in North Jersey. Each season has its advantages, and we adjust our approach based on conditions. The ground just needs to be workable.
                    </p>
                    <p>
                      Families in West Orange and Hackensack often book their installs so their patios are ready for those first warm weekends. Others prefer to get the project done in fall or winter so everything is set for spring.
                    </p>
                    <p>
                      Polymeric sand sets best when temperatures stay between 50 and 80 degrees, but we plan around that. If you're in Paramus, <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link>, Wayne, or the <Link to="/service-areas/passaic-county/paver-patio-pennington-park-paterson" className="text-secondary hover:underline">Pennington Park area of Paterson</Link>, give us a call or text and we can talk through timing that works for your schedule. We also work the tighter lots in <Link to="/service-areas/passaic-county/paver-patio-downtown-paterson" className="text-secondary hover:underline">downtown Paterson</Link>, where access and material staging get planned before anything is ordered.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={poolImage} 
                    alt="Paver patio installation around pool in Kinnelon, NJ featuring custom design and proper drainage" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Quality & Longevity Section - Image LEFT, Content RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={northHaledonPatioImage} 
                    alt="Newly installed patio pavers with outdoor kitchen, lighting, and steps in North Haledon, Passaic County, NJ, by Masonry Pro" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Quality Paver Patios Last <span className="text-gradient-gold">25 to 50 Years</span> with Proper Installation
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A paver patio is a long-term investment. When installed correctly, it can last for generations. That's the kind of return homeowners in Livingston and Short Hills want to see.
                    </p>
                    <p>
                      Concrete pavers are built to take a beating. They meet <CitationLink num={2} url="https://www.astm.org">ASTM C936 standards with 8,000 PSI strength</CitationLink>. If one cracks in the years to come, it can be easily replaced without tearing out the entire patio.
                    </p>
                    <p>
                      <CitationLink num={5} url="https://www.icpi.org">Pavers interlock, so the weight gets spread out across the surface</CitationLink>. Your patio won't crack under the outdoor furniture or foot traffic.
                    </p>
                    <p>
                      Our winters here in North Jersey put outdoor surfaces through a lot. Freeze-thaw cycles can be relentless. Water pools, turns to ice, and then expands. That cycle breaks down poorly built patios over time. Proper drainage and base work can keep that from happening. <CitationLink num={1} url="https://www.icpi.org">ICPI-certified installers</CitationLink> around Essex and Morris counties build with this in mind.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Home Value Section with Citations */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Paver Patios Add <span className="text-gradient-gold">Measurable Value</span> to North Jersey Homes
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    A paver patio does more than look good. It can <Link to="/pro-tips/benefits-of-adding-a-paver-patio" className="text-secondary hover:underline">add real value to your property</Link>. The National Association of Realtors puts patios at a <CitationLink num={4} url="https://www.nar.realtor">9.7 out of 10 on their joy score</CitationLink>. Homeowners love them, and buyers notice them.
                  </p>
                  <p>
                    When it comes time to sell your property, homeowners can expect to <CitationLink num={4} url="https://www.nar.realtor">recoup around 60% of what they spent</CitationLink> on the installation. In neighborhoods like Glen Ridge and Maplewood, outdoor living space is a selling point. Buyers shopping in Bergen and Essex counties often have it on their list.
                  </p>
                  <p>
                    A well-built patio gives your family a place to gather for years. And when you're ready to move on, it helps your home stand out from the competition. That's value you can see and value you can measure.
                  </p>
                  <p>
                    Planning a driveway project too? We install <Link to="/paving-services/paver-driveways" className="text-secondary hover:underline">paver driveways</Link> across North Jersey using the same base prep standards and materials.
                  </p>
                  <p>
                    A paver patio is a real investment, and many of our customers spread the cost over manageable monthly payments instead of paying out of pocket. Financing through our partner Acorn Finance is available with no impact to your credit score during pre-qualification. <Link to="/financing" className="text-secondary hover:underline">Learn more about financing options</Link> and see what might fit your project.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Common Mistakes Section - Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Avoiding Common Installation Mistakes <span className="text-gradient-gold">Protects Your Investment</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Some contractors rush the job. That's when problems occur. We see the same mistakes over and over in Wayne, Paramus, and across North Jersey.
                    </p>
                    <p>
                      <strong>Skipping base depth</strong> is a big one. Without enough gravel underneath, pavers start to sink within the first year. Homeowners call us to fix patios that are only a few seasons old.
                    </p>
                    <p>
                      <strong>Edge restraints matter too.</strong> Without them, pavers along the border can spread apart and separate. Gaps form, and weeds move in.
                    </p>
                    <p>
                      <strong>The wrong sand in the joints</strong> is another issue we run into. Regular sand washes out when it rains. Polymeric sand locks the pavers together and keeps weeds from taking root.
                    </p>
                    <p>
                      <strong>Clay soil</strong> is common throughout North Jersey. If there's no fabric barrier, clay works its way into the gravel base and weakens it. Frost heave becomes a real problem when that happens. <CitationLink num={1} url="https://www.icpi.org">ICPI-certified contractors</CitationLink> know to build for these conditions from the start.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={veronaImage} 
                    alt="Newly installed patio pavers with a stone retaining wall and white vinyl fence in Verona, New Jersey" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  How Long Does a Paver Patio Last in <span className="text-gradient-gold">North Jersey</span>?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  A professionally installed paver patio in North Jersey can last 25 to 50 years. Our freeze-thaw cycles put outdoor surfaces to the test each winter, so proper installation matters.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Factors that affect how long your patio will last:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground list-disc pl-6">
                  <li>A properly compacted gravel base sized for your soil and project type, per <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink></li>
                  <li>Polymeric sand in the joints to prevent erosion and weed growth</li>
                  <li>Periodic inspection and resealing every 7 to 15 years depending on wear and exposure</li>
                </ul>
                <p className="text-lg text-muted-foreground mt-6">
                  When a qualified masonry contractor handles the base prep and drainage, your patio can hold up for decades in our regional climate.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section - Card Grid */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our Work in"
            highlightedTitle="Action"
            videoIds={["Po36ZvNh7ro", "KFgLmBVqRZQ", "_8_1uw8ZHAk"]}
          />

          <RelatedServices currentCategory="paving" />

          {/* CTA Section with Form */}
          <CTASection 
            heading="Need a" 
            highlightedHeading="Patio Paver Installation?"
            serviceName="Paver Patio"
            showFinancing
          />

          {/* Citations Accordion */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base">
                      <ul className="space-y-3">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <sup>{citation.num}</sup>{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {citation.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* Hub Backlink */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-lg text-muted-foreground">
                <p>
                  Paver patios are one part of the <Link to="/paving-services" className="text-secondary hover:underline">paving work we do across North Jersey</Link>, including walkways and driveways.
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
